import { useCallback, useEffect, useReducer, useRef } from 'react';
import { ApiError } from '../../services/api';
import { getInsightsSection } from '../../services/marketingApi';
import { InsightsPlatform, InsightsSection, InsightsSectionMap } from '../../types/marketing';

export type SectionState<T> =
  | { status: 'loading' }
  | { status: 'ok'; data: T }
  | { status: 'error'; message: string; httpStatus?: number };

/**
 * Spec 084 — each tab fetches its endpoint the first time it is opened and keeps the answer per
 * (range, platform) in page state, so switching tabs back and forth costs no request. A range or platform
 * change aborts the requests in flight (AbortController); an errored tab refetches on Retry.
 */
export function useInsightsSections(token: string | null | undefined, from: string, to: string, platform: InsightsPlatform, enabled: boolean) {
  const cache = useRef(new Map<string, SectionState<unknown>>());
  const controllers = useRef(new Map<string, AbortController>());
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const keyOf = useCallback((s: InsightsSection) => `${s}|${from}|${to}|${platform}`, [from, to, platform]);

  const load = useCallback((section: InsightsSection, force = false) => {
    if (!token || !enabled) return;
    const key = keyOf(section);
    const current = cache.current.get(key);
    if (!force && current && current.status !== 'error') return;
    controllers.current.get(key)?.abort();
    const c = new AbortController();
    controllers.current.set(key, c);
    cache.current.set(key, { status: 'loading' });
    rerender();
    getInsightsSection(token, section, from, to, platform, c.signal)
      .then((data) => {
        if (c.signal.aborted) return;
        cache.current.set(key, { status: 'ok', data });
        rerender();
      })
      .catch((err) => {
        if (c.signal.aborted) {
          // Forget the half-loaded entry so coming back to this range fetches again.
          if (controllers.current.get(key) === c && cache.current.get(key)?.status === 'loading') cache.current.delete(key);
          return;
        }
        cache.current.set(key, {
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed',
          httpStatus: err instanceof ApiError ? err.status : undefined,
        });
        rerender();
      })
      .finally(() => { if (controllers.current.get(key) === c) controllers.current.delete(key); });
  }, [token, enabled, keyOf, from, to, platform]);

  useEffect(() => {
    const live = controllers.current;
    return () => {
      live.forEach((c) => c.abort());
      live.forEach((_, k) => { if (cache.current.get(k)?.status === 'loading') cache.current.delete(k); });
      live.clear();
    };
  }, [token, from, to, platform, enabled]);

  const get = useCallback(<S extends InsightsSection>(section: S) => cache.current.get(keyOf(section)) as SectionState<InsightsSectionMap[S]> | undefined, [keyOf]);
  return { get, load };
}
